import { Message, MessageContentItem } from '@/features/tools/types'
import { toast } from 'react-toastify'
import { MarkdownBuffer } from './markdown-stream-buffer'
import { handleToolUseRequest } from '@/features/tool-use'
import { ToolType, ToolUseRequestData } from '@/features/tool-use/types'
import { postToolUseResult } from '@/features/tools/api'
import { fetchEventSource } from '@microsoft/fetch-event-source'

export interface BaseStreamEvent {
  type: string
}

export interface GenericNotificationEvent extends BaseStreamEvent {
  type: 'notification'
  variant: 'info' | 'success' | 'warning' | 'error'
  message: string
}

export interface TextCreatedEvent extends BaseStreamEvent {
  type: 'textCreated'
}

export interface TextDeltaEvent extends BaseStreamEvent {
  type: 'textDelta'
  value: string | undefined
}

export interface TextDoneEvent extends BaseStreamEvent {
  type: 'textDone'
}

export enum PluginCallStatus {
  'pending' = 'pending',
  'completed' = 'completed',
  'failed' = 'failed',
}

export interface PluginCallCreatedEvent extends BaseStreamEvent {
  id: string
  type: 'pluginCallCreated'
  function: string
  displayMessage: string
}

export interface PluginCallDoneEvent extends BaseStreamEvent {
  id: string
  type: 'pluginCallDone'
}

export interface PluginUserFacingResultText {
  type: 'text'
  text: string
}

export interface PluginUserFacingResultCode {
  type: 'code'
  code: string
}

export interface PluginUserFacingResultImage {
  type: 'image'
  url: string
  prompt: string
  aspectRatio?: string
}

export interface PluginUserFacingResultLinks {
  type: 'links'
  links: {
    title: string
    url: string
  }[]
}

export interface PluginUserFacingResultSearch {
  type: 'search'
  input: {
    label: string
    value: string
  }
  output: {
    label: string
    links?: {
      title: string
      url: string
    }[]
    text?: string
  }
  footerText?: string
}

export interface PluginUserFacingResultFile {
  type: 'file'
  mimeType: string
  url: string
}

export interface PluginUserFacingResultDeepResearch {
  type: 'deepResearch'
  latency: number
  report: string
  links: {
    title: string
    url: string
  }[]
}

export interface PluginUserFacingResultAuthenticationError {
  type: 'authenticationError'
  message: string
  provider?: string
  url?: string
}

export interface PluginUserFacingResultMailList {
  type: 'mailList'
  mails: {
    id: string
    subject?: string | null
    from?: string | null
    date?: string | null
    snippet: string
  }[]
}

export interface PluginUserFacingResultMail {
  type: 'mail'
  threadId: string
  messages: {
    id: string
    subject?: string | null
    from?: string | null
    date?: string | null
    body: string
  }[]
}

export interface PluginUserFacingResultCalendarEvents {
  type: 'calendarEvents'
  events: {
    id: string
    summary: string
    start: string
    end: string
    location?: string
    htmlLink?: string
  }[]
}

export interface PluginUserFacingResultFileList {
  type: 'fileList'
  files: {
    id: string
    name: string
    mimeType: string
    webViewLink?: string
    modifiedTime?: string
  }[]
}

export interface PluginCallUpdateEvent extends BaseStreamEvent {
  id: string
  type: 'pluginCallUpdate'
  displayMessage: string
}

export type PluginUserFacingResult =
  | PluginUserFacingResultText
  | PluginUserFacingResultCode
  | PluginUserFacingResultImage
  | PluginUserFacingResultLinks
  | PluginUserFacingResultSearch
  | PluginUserFacingResultFile
  | PluginUserFacingResultDeepResearch
  | PluginUserFacingResultAuthenticationError
  | PluginUserFacingResultMailList
  | PluginUserFacingResultMail
  | PluginUserFacingResultCalendarEvents
  | PluginUserFacingResultFileList

export interface PluginCallResultEvent extends BaseStreamEvent {
  id: string
  type: 'pluginCallResult'
  success: boolean
  result: PluginUserFacingResult
  displayMessage: string
}

export interface ToolUseRequestEvent extends BaseStreamEvent {
  type: 'toolUseRequest'
  threadId: string
  signalId: string
  toolType: string
  data: Record<string, any>
}

export type StreamEvent =
  | GenericNotificationEvent
  | TextCreatedEvent
  | TextDeltaEvent
  | TextDoneEvent
  | PluginCallCreatedEvent
  | PluginCallDoneEvent
  | PluginCallResultEvent
  | PluginCallUpdateEvent
  | ToolUseRequestEvent

const safeParseEvent = (json: string): StreamEvent | null => {
  try {
    return JSON.parse(json) as StreamEvent
  } catch (error) {
    return null
  }
}

export interface ProcessEventStreamOptions {
  url: string
  method?: string
  headers?: Record<string, string>
  body?: BodyInit
  signal?: AbortSignal
}

export const processEventStream = async (
  options: ProcessEventStreamOptions,
  updateMessage: Function,
  onEvent?: (event: StreamEvent) => void,
) => {
  const message: Message = {
    role: 'assistant',
    content: [],
  }
  const mdBuffer = new MarkdownBuffer()
  let contentIndex = -1

  await fetchEventSource(options.url, {
    method: options.method || 'GET',
    headers: options.headers,
    body: options.body,
    signal: options.signal,
    credentials: 'include', // For auth cookies
    openWhenHidden: true,

    onopen: async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    },

    onmessage: (msg) => {
      // Parse the SSE data field as JSON
      const event = safeParseEvent(msg.data)

      if (!event) {
        console.error('Invalid event data:', msg.data)
        return
      }

      if (onEvent) {
        onEvent(event)
      }

      if (event.type === 'notification') {
        switch (event.variant) {
          case 'info':
            toast(event.message)
            break
          case 'success':
            toast.success(event.message)
            break
          case 'warning':
            toast.warn(event.message)
            break
          case 'error':
            toast.error(event.message)
            break
        }
      } else if (event.type === 'textCreated') {
        message.content.push({
          type: 'text',
          text: '',
        })
        contentIndex++
        updateMessage(message)
      } else if (event.type === 'textDelta') {
        const completedFragments = mdBuffer.processText(event.value || '')
        for (const fragment of completedFragments) {
          message.content[contentIndex].text += fragment
        }

        updateMessage(message)
      } else if (event.type === 'pluginCallCreated') {
        message.content.push({
          type: 'pluginCall',
          id: event.id,
          function: event.function,
          status: PluginCallStatus.pending,
          result: undefined,
          displayMessage: event.displayMessage,
        })
        contentIndex++
        updateMessage(message)
      } else if (event.type === 'pluginCallUpdate') {
        patchPluginCall(message, event.id, (content) => ({
          ...content,
          displayMessage: event.displayMessage,
        }))
        updateMessage(message)
      } else if (event.type === 'pluginCallResult') {
        patchPluginCall(message, event.id, (content) => ({
          ...content,
          status: event.success
            ? PluginCallStatus.completed
            : PluginCallStatus.failed,
          result: event.result,
          displayMessage: event.displayMessage,
        }))
        updateMessage(message)
      } else if (event.type === 'toolUseRequest') {
        ;(async () => {
          try {
            const toolRequest: ToolUseRequestData = {
              threadId: event.threadId,
              signalId: event.signalId,
              toolType: event.toolType as ToolType,
              data: event.data,
            }

            const result = await handleToolUseRequest(toolRequest)

            await postToolUseResult(event.threadId, event.signalId, result)
          } catch (error) {
            console.error('Error executing tool:', error)
          }
        })()
      }
    },

    onerror: (error) => {
      console.error('SSE error:', error)
      throw error
    },
  })
}

function patchPluginCall(
  message: Message,
  id: string,
  updater: (content: MessageContentItem) => MessageContentItem,
) {
  const index = message.content.findIndex((content) => content.id === id)
  if (index === -1) {
    console.error('Plugin call update event received for unknown plugin call')
    return
  }
  const result = updater(message.content[index])
  if (result) {
    message.content[index] = result
  }
}
